/**
 * shadcn/Tailwind replacement for RecentPeopleStrip.
 * Horizontal scroll strip of recently viewed people.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Clock, X } from 'lucide-react';
import { photoService } from '../../services/entityServiceSingleton';
import { getRecentPeople, clearRecentPeople, type RecentPerson } from './RecentPeopleStrip';
import { cn } from '../../lib/utils';

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || '?';
}

const SUBTYPE_COLORS: Record<string, string> = {
    employee: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200',
    student: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
    contractor: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200',
    applicant: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
    public: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    sponsored: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
    shared: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200',
    vacancy: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200',
};

function MiniPersonChip({ person, onNavigate }: { person: RecentPerson; onNavigate: (hash: string) => void }) {
    const [photoData, setPhotoData] = useState<string | null>(null);

    useEffect(() => {
        if (!person.id) return;
        const cached = photoService.getCachedPhoto(person.id);
        if (cached?.Photo) setPhotoData(cached.Photo);
    }, [person.id]);

    const avatarColorClass = SUBTYPE_COLORS[person.entityType.toLowerCase()] || SUBTYPE_COLORS.public;
    const hash = `#Person/${person.entityType.toLowerCase()}/view/${person.id}`;

    return (
        <button
            onClick={() => onNavigate(hash)}
            className={cn(
                'group flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2',
                'hover:bg-muted/50 hover:shadow-sm transition-all duration-150 cursor-pointer',
                'shrink-0',
            )}
            title={`View ${person.displayName}`}
        >
            {photoData ? (
                <img
                    src={`data:image/jpeg;base64,${photoData}`}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                />
            ) : (
                <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold', avatarColorClass)}>
                    {getInitials(person.displayName)}
                </div>
            )}
            <div className="min-w-0">
                <div className="text-xs font-semibold text-foreground truncate max-w-[120px]">
                    {person.displayName}
                </div>
                <div className="text-[10px] text-muted-foreground truncate max-w-[120px]">
                    {person.entityType}
                </div>
            </div>
        </button>
    );
}

export interface ShadcnRecentPeopleStripProps {
    onNavigateHash?: (hash: string) => void;
}

export const ShadcnRecentPeopleStrip: React.FC<ShadcnRecentPeopleStripProps> = ({ onNavigateHash }) => {
    const [recentPeople, setRecentPeople] = useState<RecentPerson[]>([]);

    const refreshRecent = useCallback(() => {
        setRecentPeople(getRecentPeople());
    }, []);

    useEffect(() => {
        refreshRecent();
        const handler = () => refreshRecent();
        window.addEventListener('hades-recent-people-updated', handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('hades-recent-people-updated', handler);
            window.removeEventListener('storage', handler);
        };
    }, [refreshRecent]);

    if (recentPeople.length === 0) return null;

    const handleNavigate = (hash: string) => {
        if (onNavigateHash) {
            onNavigateHash(hash);
        } else {
            window.location.hash = hash.startsWith('#') ? hash.substring(1) : hash;
        }
    };

    return (
        <div className="mx-6 mb-4 rounded-lg border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Clock size={14} className="text-muted-foreground/50" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Recent People
                    </span>
                </div>
                <button
                    onClick={clearRecentPeople}
                    className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="Clear recent history"
                >
                    <X size={14} />
                </button>
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1">
                {recentPeople.map((person) => (
                    <MiniPersonChip
                        key={person.id}
                        person={person}
                        onNavigate={handleNavigate}
                    />
                ))}
            </div>
        </div>
    );
};
